import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { StrategySnapshot } from "@/lib/strategy-snapshot";
import type { Strategy } from "@/mocks/operating-strategy";
import { getProductDecision, getProductTacticContext } from "@/mocks/strategy-product";
import { ProductForecast } from "./ProductForecast";
import type { useStrategyWizard } from "./use-strategy-wizard";

interface PlanStepProps {
  onShowDetail: (strategy: Strategy) => void;
  productSnapshot?: StrategySnapshot;
  tactics: Strategy[];
  wizard: ReturnType<typeof useStrategyWizard>;
}

export function PlanStep({ onShowDetail, productSnapshot, tactics, wizard }: PlanStepProps) {
  const busy = wizard.busy !== null;
  return (
    <>
      {wizard.busy === "plan" ? (
        <div
          role="status"
          className="flex min-h-64 flex-col items-center justify-center gap-4 rounded-[10px] border border-line"
        >
          <Spinner className="size-6" />
          <h2 className="text-lg font-semibold">正在整理策略决策</h2>
          <p className="text-sm text-muted-foreground">
            正在依据运营报告和关键词筛选整理投放安排。
          </p>
        </div>
      ) : wizard.validPlan && productSnapshot ? (
        <>
          <ProductForecast
            snapshot={productSnapshot}
            goal={wizard.settings.goal}
            targetRank={wizard.settings.rank}
          />
          <div className="rounded-[10px] border border-line bg-muted/50 p-5">
            <h3 className="text-sm font-semibold">
              策略决策：{getProductDecision(productSnapshot, wizard.settings.goal).title}
            </h3>
            <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
              {getProductDecision(productSnapshot, wizard.settings.goal).summary}
            </p>
            <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
              {getProductDecision(productSnapshot, wizard.settings.goal).rationale}
            </p>
            <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
              {getProductDecision(productSnapshot, wizard.settings.goal).budget}
            </p>
          </div>
          <FieldGroup>
            <Field invalid={Boolean(wizard.nameError)}>
              <div className="flex w-full items-center gap-4">
                <FieldLabel htmlFor="strategy-name" className="shrink-0 whitespace-nowrap">
                  自定义策略名称
                </FieldLabel>
                <Input
                  id="strategy-name"
                  className="max-w-[440px]"
                  value={wizard.name}
                  disabled={busy}
                  aria-invalid={Boolean(wizard.nameError)}
                  aria-describedby="strategy-name-error"
                  onChange={(event) => {
                    wizard.setName(event.target.value);
                    wizard.setNameError("");
                  }}
                />
                <span className="text-xs text-muted-foreground">可修改 · 1–50 字</span>
              </div>
              {wizard.nameError && (
                <p
                  id="strategy-name-error"
                  className="text-xs text-destructive-foreground"
                  role="alert"
                >
                  {wizard.nameError}
                </p>
              )}
            </Field>
          </FieldGroup>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">推荐打法</h2>
              <p className="mt-1 text-[13px] text-muted-foreground">
                SKAG、瀑布流、自动采词、低效词复核与利润红线核验。
              </p>
            </div>
            <Badge variant="neutral">{tactics.length} 种打法</Badge>
          </div>
          <div className="flex flex-col gap-3">
            {tactics.map((tactic, i) => {
              const context = getProductTacticContext(
                productSnapshot,
                tactic.id,
                wizard.settings.goal,
              );
              return (
                <div
                  className="flex items-center gap-4 rounded-[10px] border border-line p-5"
                  key={tactic.id}
                >
                  <span className="w-7 text-xs text-muted-foreground">0{i + 1}</span>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold">{tactic.name}</h3>
                    <p className="mt-1 text-[13px] leading-[21px] text-muted-foreground">
                      {context?.reason ?? tactic.description}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      执行重点：
                      {context?.sections.find((section) => section.title === "执行安排")?.body ??
                        "请查看详情并核对执行条件。"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      onShowDetail(
                        context
                          ? {
                              ...tactic,
                              asin: productSnapshot.asin,
                              description: context.reason,
                              sections: context.sections,
                            }
                          : tactic,
                      );
                    }}
                  >
                    查看详情
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      ) : wizard.validPlan ? (
        <>
          <div className="rounded-[10px] border border-line bg-muted/50 p-5">
            <h2 className="text-sm font-semibold">预算与策略</h2>
            <p className="mt-2 text-[13px] leading-6 text-muted-foreground">
              当前商品尚无已核对的经营指标。请结合商品资料核对预算与投放目标后，再决定是否执行。
            </p>
          </div>
          <FieldGroup>
            <Field invalid={Boolean(wizard.nameError)}>
              <div className="flex w-full items-center gap-4">
                <FieldLabel htmlFor="strategy-name" className="shrink-0 whitespace-nowrap">
                  自定义策略名称
                </FieldLabel>
                <Input
                  id="strategy-name"
                  className="max-w-[440px]"
                  value={wizard.name}
                  disabled={busy}
                  aria-invalid={Boolean(wizard.nameError)}
                  onChange={(event) => {
                    wizard.setName(event.target.value);
                    wizard.setNameError("");
                  }}
                />
              </div>
              {wizard.nameError && (
                <p className="text-xs text-destructive-foreground" role="alert">
                  {wizard.nameError}
                </p>
              )}
            </Field>
          </FieldGroup>
          <h2 className="text-lg font-semibold">推荐打法</h2>
          <div className="flex flex-col gap-3">
            {tactics.map((tactic) => (
              <div className="rounded-[10px] border border-line p-5" key={tactic.id}>
                <h3 className="text-sm font-semibold">{tactic.name}</h3>
                <p className="mt-1 text-[13px] text-muted-foreground">{tactic.description}</p>
                <Button className="mt-3" variant="outline" onClick={() => onShowDetail(tactic)}>
                  查看详情
                </Button>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {wizard.error && (
        <ErrorState
          message={wizard.error}
          onRetry={wizard.validPlan ? wizard.createStrategy : () => void wizard.generatePlan()}
          variant="inline"
        />
      )}
    </>
  );
}
